import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import AdminLayout from "../../components/layout/AdminLayout";
import { getProducts } from "../../redux/actions/productActions";
import { getCategories } from "../../redux/actions/categoryActions";
import { getOrders } from "../../redux/actions/orderActions";
import { getCustomers } from "../../redux/actions/customerActions";
import { getTransactions } from "../../redux/actions/transactionActions";
import { ROUTES } from "../../config/routing";
import { MdMoney, MdPerson4, MdProductionQuantityLimits, MdShopTwo } from "react-icons/md";

const Dashboard = () => {
  const dispatch = useDispatch();
  const { products, loading: productsLoading } = useSelector((state) => state.products);
  const { categories, loading: categoriesLoading } = useSelector((state) => state.categories);
  const { orders, loading: ordersLoading } = useSelector((state) => state.orders);
  const { customers, loading: customersLoading } = useSelector((state) => state.customers);
  const { transactions, loading: transactionsLoading } = useSelector((state) => state.transactions);

  const [stats, setStats] = useState({
    totalRevenue: 0,
    pendingOrders: 0,
    lowStockProducts: 0,
  });

  useEffect(() => {
    dispatch(getProducts());
    dispatch(getCategories());
    dispatch(getOrders());
    dispatch(getCustomers());
    dispatch(getTransactions());
  }, [dispatch]);

  useEffect(() => {
    if (!productsLoading && !ordersLoading && !transactionsLoading) {
      // Calcul du chiffre d'affaires total
      const totalRevenue = orders.reduce((sum, order) => {
        return order.status? sum + order.quantity * order?.product?.price || 0 : sum;
        
      }, 0);

      // Nombre de commandes en attente
      const pendingOrders = orders.filter((order) => order.status === "pending").length;

      // Produits en stock faible (< 10)
      const lowStockProducts = products.filter((product) => product.stock < 10).length;

      setStats({ totalRevenue, pendingOrders, lowStockProducts });
    }
  }, [products, orders, transactions, productsLoading, ordersLoading, transactionsLoading]);

  const isLoading = productsLoading || categoriesLoading || ordersLoading || customersLoading || transactionsLoading;

  // Récupérer les dernières commandes (5 plus récentes)
  const recentOrders = [...orders]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5);

  return (
    <AdminLayout>
      <div className="container-fluid">
        <h1 className="h2 mt-4 mb-4 text-dark">Tableau de bord</h1>

        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "60vh" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="row g-4 mb-4">
              {/* Total Revenue */}
              <div className="col-md-6 col-lg-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body d-flex align-items-center">
                    <div className=" bg-opacity-10 rounded-circle p-3 me-3">
                      <MdMoney size={32} className="text-success" />
                    </div>
                    <div>
                      <p className="text-muted small mb-0">Chiffre d'affaires</p>
                      <h5 className="mb-0">{stats.totalRevenue.toFixed(2)} F</h5>
                    </div>
                  </div>
                </div>
              </div>

              {/* Products */}
              <div className="col-md-6 col-lg-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body d-flex align-items-center">
                    <div className=" bg-opacity-10 rounded-circle p-3 me-3">
                      <MdProductionQuantityLimits size={32} className="text-dark" />
                      
                    </div>
                    <div>
                      <p className="text-muted small mb-0">Total Produits</p>
                      <h5 className="mb-0">{products.length}</h5>
                    </div>
                  </div>
                  <div className="card-footer py-2 bg-transparent border-top-0">
                    <Link to={`/${ROUTES.ADMIN_PRODUCTS}`} className="small link-dark  underline">
                      Voir tous les produits
                    </Link>
                  </div>
                </div>
              </div>

              {/* Orders */}
              <div className="col-md-6 col-lg-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body d-flex align-items-center">
                    <div className="bg-opacity-10 rounded-circle p-3 me-3">
                      <MdShopTwo size={32} className="text-dark" />
                    </div>
                    <div>
                      <p className="text-muted small mb-0">Commandes en attente</p>
                      <h5 className="mb-0">{stats.pendingOrders}</h5>
                    </div>
                  </div>
                  <div className="card-footer py-2 bg-transparent border-top-0">
                    <Link to={`/${ROUTES.ADMIN_ORDERS}`} className="small link-dark text-decoration-none">
                      Voir toutes les commandes
                    </Link>
                  </div>
                </div>
              </div>

              {/* Customers */}
              <div className="col-md-6 col-lg-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body d-flex align-items-center">
                    <div className=" bg-opacity-10 rounded-circle p-3 me-3">
                      <MdPerson4 size={32} className="text-purple" />
                    </div>
                    <div>
                      <p className="text-muted small mb-0">Clients</p>
                      <h5 className="mb-0">{customers.length}</h5>
                    </div>
                  </div>
                  <div className="card-footer py-2 bg-transparent border-top-0">
                    <Link to="#" className="small link-dark text-decoration-none">
                      Voir tous les clients
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              {/* Recent Orders */}
              <div className="col-lg-6 mb-4">
                <div className="card shadow-sm">
                  <div className="card-header">
                    <h5 className="card-title mb-0">Commandes récentes</h5>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>ID</th>
                            <th>Client</th>
                            <th>Date</th>
                            <th>Statut</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentOrders.map((order) => (
                            <tr key={order._id}>
                              <td>
                                <Link to={`/admin/orders/${order._id}`} className="link-dark">
                                  #{order._id.substring(0, 8)}
                                </Link>
                              </td>
                              <td>{order.firstName} {order.lastName}</td>
                              <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                              <td>
                                <span className={`badge ${
                                  order.status === "pending"
                                    ? "bg-warning text-dark"
                                    : order.status === "processing"
                                    ? "bg-info"
                                    : order.status === "shipped"
                                    ? "bg-secondary"
                                    : order.status === "delivered"
                                    ? "bg-success"
                                    : "bg-danger"
                                }`}>
                                  {order.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                          {recentOrders.length === 0 && (
                            <tr>
                              <td colSpan="4" className="text-center text-muted">
                                Aucune commande récente
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {orders.length > 5 && (
                      <div className="card-footer bg-white pt-2 pb-2">
                        <Link to={`/${ROUTES.ADMIN_ORDERS}`} className="small link-primary text-decoration-none float-end">
                          Voir toutes les commandes
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Low Stock Products */}
              <div className="col-lg-6 mb-4">
                <div className="card shadow-sm">
                  <div className="card-header">
                    <h5 className="card-title mb-0">Produits en stock faible</h5>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Produit</th>
                            <th>Catégorie</th>
                            <th>Stock</th>
                            <th>Prix</th>
                          </tr>
                        </thead>
                        <tbody>
                          {products
                            .filter((product) => product.stock < 10)
                            .slice(0, 5)
                            .map((product) => (
                              <tr key={product._id}>
                                <td>
                                  <Link to={`/admin/products/${product._id}`} className="link-primary">
                                    {product.name}
                                  </Link>
                                </td>
                                <td>{product.category?.name || "N/A"}</td>
                                <td>
                                  <span className={`badge ${
                                    product.stock === 0 ? "bg-danger" :
                                    product.stock < 5 ? "bg-warning text-dark" :
                                    "bg-info"
                                  }`}>
                                    {product.stock}
                                  </span>
                                </td>
                                <td>{product.price.toFixed(2)} F</td>
                              </tr>
                            ))}
                          {products.filter((product) => product.stock < 10).length === 0 && (
                            <tr>
                              <td colSpan="4" className="text-center text-muted">
                                Aucun produit en stock faible
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {products.filter((product) => product.stock < 10).length > 5 && (
                      <div className="card-footer bg-white pt-2 pb-2">
                        <Link to={`/${ROUTES.ADMIN_PRODUCTS}`} className="small link-primary text-decoration-none float-end">
                          Voir tous les produits
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default Dashboard;